import { useContext, useMemo, type ReactNode } from 'react'
import {
    UNSAFE_NavigationContext as NavigationContext,
    type Navigator
} from 'react-router-dom'

/**
 * Kiosk terminals must not accumulate browser history. Every in-app
 * navigation is forced to replace the current entry, and history.go(±n)
 * (browser Back/Forward) is ignored at the navigator layer.
 *
 * Edge swipe-back still fires a real popstate; useKioskHistoryGuard traps
 * those in capture phase. This wrapper stops the stack from growing.
 */
export function KioskReplaceNavigator({ children }: { children: ReactNode }) {
    const navigationContext = useContext(NavigationContext)

    const navigator = useMemo<Navigator>(() => {
        const inner = navigationContext.navigator
        return {
            ...inner,
            push(to, state, opts) {
                inner.replace(to, state, opts)
            },
            replace(to, state, opts) {
                inner.replace(to, state, opts)
            },
            go(delta) {
                // Browser Back/Forward / Edge swipe — ignore. In-app "back"
                // buttons navigate to an explicit path instead of go(-1).
                if (delta !== 0) {
                    return
                }
                inner.go(delta)
            }
        }
    }, [navigationContext.navigator])

    return (
        <NavigationContext.Provider
            value={{ ...navigationContext, navigator }}
        >
            {children}
        </NavigationContext.Provider>
    )
}
